/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListSlackPersonAccountsRequest,
  ListSlackPersonAccountsSecurity,
} from "../models/operations/listslackpersonaccounts.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSlackPersonAccountsQuery,
  prefetchSlackPersonAccounts,
  queryKeySlackPersonAccounts,
  SlackPersonAccountsQueryData,
} from "./slackPersonAccounts.core.js";
export {
  buildSlackPersonAccountsQuery,
  prefetchSlackPersonAccounts,
  queryKeySlackPersonAccounts,
  type SlackPersonAccountsQueryData,
};

export type SlackPersonAccountsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listPersonAccounts slackDirectoryConnections
 *
 * @remarks
 * Read mapped Slack accounts for an active organization person by exact Speakeasy user ID. Browser session only; caller must be that person or an organization administrator. Does not infer associations from email or grant permissions.
 */
export function useSlackPersonAccounts(
  request: ListSlackPersonAccountsRequest,
  security?: ListSlackPersonAccountsSecurity | undefined,
  options?: QueryHookOptions<
    SlackPersonAccountsQueryData,
    SlackPersonAccountsQueryError
  >,
): UseQueryResult<SlackPersonAccountsQueryData, SlackPersonAccountsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildSlackPersonAccountsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listPersonAccounts slackDirectoryConnections
 *
 * @remarks
 * Read mapped Slack accounts for an active organization person by exact Speakeasy user ID. Browser session only; caller must be that person or an organization administrator. Does not infer associations from email or grant permissions.
 */
export function useSlackPersonAccountsSuspense(
  request: ListSlackPersonAccountsRequest,
  security?: ListSlackPersonAccountsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SlackPersonAccountsQueryData,
    SlackPersonAccountsQueryError
  >,
): UseSuspenseQueryResult<
  SlackPersonAccountsQueryData,
  SlackPersonAccountsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSlackPersonAccountsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setSlackPersonAccountsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      userId: string;
      cursor?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: SlackPersonAccountsQueryData,
): SlackPersonAccountsQueryData | undefined {
  const key = queryKeySlackPersonAccounts(...queryKeyBase);

  return client.setQueryData<SlackPersonAccountsQueryData>(key, data);
}

export function invalidateSlackPersonAccounts(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      userId: string;
      cursor?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "slackDirectoryConnections",
      "listPersonAccounts",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllSlackPersonAccounts(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "slackDirectoryConnections",
      "listPersonAccounts",
    ],
  });
}
